import { useQuery } from '@tanstack/react-query';
import { ActivityLogs, type ActivityLogEntry } from './api';
import { useBackendOnline } from '@/store/connection';

/**
 * Bugüne ait özet metrikler. Workspace üst kartlarını ve hero text'i
 * besler. Aynı /api/activity-logs/me endpoint'inden çekilir — useActivityFeed
 * ile cache'i React Query üzerinden paylaşır (key farklı ama veri kaynağı aynı).
 *
 * Hesaplanan alanlar SADECE activity_logs'tan çekilebilenler. Live queue
 * stats (kuyrukta bekleyen sayısı, ort. bekleme) ayrı endpoint gerektirir,
 * şu an UI'da gizleniyor.
 */
export interface TodayStats {
  /** Bugün cevaplanan inbound çağrı sayısı (call_answered event'leri). */
  callsAnswered: number;
  /** Bugün cevapsız kalan inbound çağrı sayısı (incoming_call - answered - rejected). */
  callsMissed: number;
  /** Bugün başlatılan outbound çağrı sayısı. */
  callsOutbound: number;
  /** Bugün yapılan aktarım sayısı. */
  transfers: number;
  /** Ortalama görüşme süresi (saniye). 0 = hiç tamamlanmış çağrı yok. */
  avgCallSec: number;
  /** En uzun tek çağrı süresi (saniye). */
  maxCallSec: number;
  /** Bugün toplam görüşme süresi (saniye). */
  totalTalkSec: number;
  /** Aktif mola sayısı (queue pause). */
  breakCount: number;
  /** Bugün ilk aktivite ile şu an arası — yaklaşık session uzunluğu. */
  sessionSec: number;
  /** Gelen (inbound) yön kırılımı — eski panel 4. kolonu. */
  inbound: InboundStats;
  /** Giden (outbound) yön kırılımı. */
  outbound: OutboundStats;
}

export interface InboundStats {
  /** Toplam gelen çağrı (cevaplanan + cevapsız). */
  total: number;
  answered: number;
  missed: number;
  /** Ort. konuşma (cevaplanan, saniye). */
  avgSec: number;
  /** En uzun konuşma (saniye). */
  maxSec: number;
  /** Toplam bekletme (hold, saniye). */
  holdSec: number;
  /** Toplam sessize alma (mute, saniye). */
  muteSec: number;
}

export interface OutboundStats {
  total: number;
  avgSec: number;
  maxSec: number;
  holdSec: number;
  muteSec: number;
}

const EMPTY_INBOUND: InboundStats = { total: 0, answered: 0, missed: 0, avgSec: 0, maxSec: 0, holdSec: 0, muteSec: 0 };
const EMPTY_OUTBOUND: OutboundStats = { total: 0, avgSec: 0, maxSec: 0, holdSec: 0, muteSec: 0 };

function startOfTodayMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function computeStats(logs: ActivityLogEntry[]): TodayStats {
  const todayStart = startOfTodayMs();
  let callsAnswered = 0;
  let callsOutbound = 0;
  let callsMissed = 0;
  let transfers = 0;
  let totalTalk = 0;
  let maxCall = 0;
  let callsCounted = 0;
  let breakCount = 0;
  let earliestTs = Date.now();
  // Yön bazlı akümülatörler (eski panel INBOUND/OUTBOUND kolonları)
  const inb = { total: 0, answered: 0, missed: 0, talkSec: 0, connected: 0, maxSec: 0, holdSec: 0, muteSec: 0 };
  const out = { total: 0, talkSec: 0, connected: 0, maxSec: 0, holdSec: 0, muteSec: 0 };

  for (const l of logs) {
    const ts = new Date(l.createdAt).getTime();
    if (ts < todayStart) continue;
    if (ts < earliestTs) earliestTs = ts;
    const meta = (l.meta ?? {}) as Record<string, any>;

    if (l.type === 'call_answered') {
      callsAnswered += 1;
    } else if (l.type === 'call_started') {
      callsOutbound += 1;
    } else if (l.type === 'call_transferred') {
      transfers += 1;
    } else if (l.type === 'call_ended') {
      const ms = typeof meta.duration_ms === 'number' ? meta.duration_ms : 0;
      const sec = ms > 0 ? Math.round(ms / 1000) : 0;
      if (sec > 0) {
        totalTalk += sec;
        if (sec > maxCall) maxCall = sec;
        callsCounted += 1;
      }
      // Cevapsız = cevaplanmadan biten GELEN çağrı. SAĞLAM sinyal: call_ended'in
      // answered flag'i (yoksa duration>0 fallback). Eski "gelen-cevaplanan" çıkarma
      // formülü asimetrik loglamada (answered>incoming) clamp ile yanlış 0 veriyordu.
      const answeredFlag = meta.answered === true || ms > 0;
      const holdSec = typeof meta.hold_ms === 'number' ? Math.round(meta.hold_ms / 1000) : 0;
      const muteSec = typeof meta.mute_ms === 'number' ? Math.round(meta.mute_ms / 1000) : 0;
      if (meta.direction === 'inbound') {
        inb.total += 1;
        inb.holdSec += holdSec;
        inb.muteSec += muteSec;
        if (answeredFlag) {
          inb.answered += 1;
          if (sec > 0) { inb.talkSec += sec; inb.connected += 1; if (sec > inb.maxSec) inb.maxSec = sec; }
        } else {
          inb.missed += 1;
          callsMissed += 1;
        }
      } else if (meta.direction === 'outbound') {
        out.total += 1;
        out.holdSec += holdSec;
        out.muteSec += muteSec;
        if (sec > 0) { out.talkSec += sec; out.connected += 1; if (sec > out.maxSec) out.maxSec = sec; }
      }
    } else if (l.type === 'queue_paused') {
      breakCount += 1;
    }
  }
  const avgCall = callsCounted > 0 ? Math.round(totalTalk / callsCounted) : 0;
  const sessionSec = Math.max(0, Math.floor((Date.now() - earliestTs) / 1000));
  return {
    callsAnswered, callsMissed, callsOutbound, transfers,
    avgCallSec: avgCall, maxCallSec: maxCall, totalTalkSec: totalTalk,
    breakCount, sessionSec,
    inbound: {
      total: inb.total, answered: inb.answered, missed: inb.missed,
      avgSec: inb.connected > 0 ? Math.round(inb.talkSec / inb.connected) : 0,
      maxSec: inb.maxSec, holdSec: inb.holdSec, muteSec: inb.muteSec,
    },
    outbound: {
      total: out.total,
      avgSec: out.connected > 0 ? Math.round(out.talkSec / out.connected) : 0,
      maxSec: out.maxSec, holdSec: out.holdSec, muteSec: out.muteSec,
    },
  };
}

export function useTodayStats(): TodayStats {
  const backendOnline = useBackendOnline();
  const { data } = useQuery({
    queryKey: ['today-stats'],
    queryFn: () => ActivityLogs.listMine(200),
    refetchInterval: 15_000,
    staleTime: 10_000,
    enabled: backendOnline, // backend offline'ken poll'u durdur
  });
  if (!data) {
    return {
      callsAnswered: 0, callsMissed: 0, callsOutbound: 0, transfers: 0,
      avgCallSec: 0, maxCallSec: 0, totalTalkSec: 0,
      breakCount: 0, sessionSec: 0,
      inbound: EMPTY_INBOUND, outbound: EMPTY_OUTBOUND,
    };
  }
  return computeStats(data);
}

/** "01:23" veya "12:34" formatı. */
export function fmtMSS(sec: number): string {
  if (sec <= 0) return '00:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** "01:23:45" formatı. */
export function fmtHHMMSS(sec: number): string {
  if (sec <= 0) return '00:00:00';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
